<template>
  <v-row no-gutters class="fill-height flex-column" style="overflow-y: auto; overflow-x: hidden;">
    <v-col v-if="isFetched && mapPanelDataFetched" cols="12" class="pr-6">
      <card-title-row :straightTitle="$i18n.t('sidebar.failuresTwoSummary.title')" class="pt-5 pb-5">
        <template #buttons v-if="layerPermissions.edit">
          <data-input
            :dataType="{ name: 'geometry' }"
            name="failuresSummaryNewFeatureGeometry"
            button-component="baseRoundedButton"
            :buttonProps="{
              icon: 'mdi-plus',
              size: 'x-small',
              disableDefaultMargin: true,
            }"
            :geometry-type="layerGeometryType"
            :tooltip="$i18n.t('sidebar.failuresTwoSummary.addNewOne')"
            v-model="featureToAddGeometry"
            @status="statusChanged"
          />
        </template>
      </card-title-row>
      <template v-for="(summaryElement, idx) of summaryLists">
        <sidebar-row :key="idx">
          <ribbon-list
            v-if="(summaryFeatures[summaryElement.name] || []).length"
            :key="idx"
            :listItems="summaryFeatures[summaryElement.name]"
            item-title-key="desc"
            :title-path="summaryElement.titlePath"
            class="pb-7"
            show-title-count-badge
            :title-count-badge-color="summaryElement.color"
          />
        </sidebar-row>
      </template>
    </v-col>
    <main-loader v-else />
  </v-row>
</template>
<script>
import { call, get, sync } from 'vuex-pathify';

import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';
import MainLoader from '@/components/MainLoader';
import RibbonList from '@/components/RibbonList';
import SidebarRow from '@/components/SidebarRow';

export default {
  name: 'FailuresSummary',
  components: { CardTitleRow, DataInput, MainLoader, RibbonList, SidebarRow },
  computed: {
    mapPanelDataFetched: get('map/mapPanelDataFetched'),
    dataSources: get('layers/metadata'),
    layers: get('layers/layers'),
    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    dataSourceName: get('admin/modulesMapping@failures_2.datasource'),
    layerId: get('admin/modulesMapping@failures_2.settings.layer_id'),
    statusAttributeName: get('admin/modulesMapping@failures_2.settings.status_attribute'),
    statusNewValue: get('admin/modulesMapping@failures_2.settings.status_new_value'),
    statusActiveValue: get('admin/modulesMapping@failures_2.settings.status_active_value'),
    newFeatureGeometry: sync('edit/newFeatureGeometry'),
    layer() {
      return this.layers?.[this.layerId] || undefined;
    },
    layerGeometryType() {
      return this.layer?.geometry_type || '';
    },
    dataSource() {
      return this.dataSources?.[this.dataSourceName] || undefined;
    },
    dataSourceUseQualifiedNames() {
      return this.dataSource?.attributes_use_datasource_qualified_names || false;
    },
    summaryLists() {
      return [
        {
          name: 'new',
          statusValue: this.statusNewValue,
          titlePath: 'sidebar.failuresTwoSummary.newFailures',
          color: 'error',
        },
        {
          name: 'active',
          statusValue: this.statusActiveValue,
          titlePath: 'sidebar.failuresTwoSummary.activeFailures',
          color: 'edit',
        },
      ];
    },
    layersPermissions: get('users/layersPermissions@:layerId'),
    layerPermissions() {
      const value = this.layersPermissions?.main_value || 0;
      return {
        edit: value > 1,
        preview: value > 0,
        value,
      };
    },
  },
  data: () => ({
    featureToAddGeometry: null,
    summaryFeatures: {},
    isFetched: false,
  }),
  methods: {
    getLayerFeatures: call('layers/getLayerFeatures'),
    async init() {
      this.summaryFeatures = {};
      try {
        this.addFailuresLayerToProject();
        const promises = [];
        this.summaryLists.forEach(summaryElement => {
          promises.push(this.getStatusValueFeatures(summaryElement));
        });
        const r = await Promise.all(promises);
        this.summaryLists.forEach((summaryElement, idx) => {
          this.summaryFeatures[summaryElement.name] = (r[idx]?.data?.data?.ids || []).map(item => {
            return { ...item, itemClickHandler: this.pushToFeature };
          });
        });
      } finally {
        this.isFetched = true;
      }
    },
    getStatusValueFeatures(summaryElement) {
      const features_filter = this.getStatusValueFilter(summaryElement.statusValue);
      const payload = {
        layer_id: this.layerId,
        features_filter,
        params: { ids_descs_only: true },
      };
      return this.getLayerFeatures(payload);
    },
    getStatusValueFilter(value) {
      const attributeFilterName = this.dataSourceUseQualifiedNames
        ? this.statusAttributeName
        : `${this.dataSourceName}.${this.statusAttributeName}`;
      return {
        $EQUAL: {
          [attributeFilterName]: value,
        },
      };
    },
    pushToFeature(item) {
      if (!item?.id && item?.id !== 0) return;
      this.$router.push({
        name: 'layerFeature',
        params: { lid: this.layerId, fid: item.id },
      });
    },
    addFailuresLayerToProject() {
      if (!this.mapPanelDataFetched || this.projectLayers.map(layer => layer.id).includes(this.layerId)) return;
      const layer = JSON.parse(JSON.stringify(this.layer));
      if (layer) {
        layer.labels_visible = layer.labels_visible || false;
        layer.style = layer.style_web;
        delete layer.style_web;
        this.$store.set('layers/ADD_PROJECT_LAYERS!', [
          { layer_id: layer.id, visible: true, opacity: 1, has_permission: true, ...layer },
        ]);
        this.$root.$emit('pushProjectLayers', [
          { layer_id: layer.id, visible: true, opacity: 1, has_permission: true, ...layer },
        ]);
        this.$store.set('snackbar/PUSH_MESSAGE!', {
          message: this.$i18n.t('snackbar.layerAddedToProject', { name: layer.name }),
        });
      }
    },
    statusChanged(nV) {
      if (nV) {
        this.$store.set('snackbar/PUSH_MESSAGE!', {
          message: this.$i18n.t('sidebar.failuresTwoSummary.newFeatureSnackbar'),
        });
      }
    },
  },
  watch: {
    mapPanelDataFetched: {
      handler(nV) {
        if (nV) this.init();
      },
      immediate: true,
    },
    featureToAddGeometry(nV) {
      if (nV) {
        this.newFeatureGeometry = nV;
        this.$router.push({
          name: 'layerFeature',
          params: {
            ...this.$route.params,
            lid: this.layerId,
            fid: 'new',
          },
        });
      }
    },
  },
  mounted() {
    this.addFailuresLayerToProject();
  },
};
</script>
